// ============================================================
// useComponentMetric — Hook per tracciare navigazione utente
// tramite span OTLP. Ogni pagina diventa uno span figlio dello
// span radice di sessione, permettendo di ricostruire il
// journey completo in Grafana Tempo.
//
// Uso: const metric = useComponentMetric('luogo');
//      metric.trackView();
//      metric.trackError('qualcosa è andato storto', error.stack);
//
// Non serve più chiamare trackView manualmente: l'hook lo fa
// automaticamente al mount via useEffect. trackView() è
// mantenuto per compatibilità ma di solito non serve chiamarlo.
// ============================================================

import { useEffect, useRef, useCallback } from 'react';
import {
  trackSpanPageStart,
  trackSpanPageEnd,
  trackSpanPageError,
  trackComponentView,
  trackComponentDuration,
  trackComponentError,
  trackPagePath,
  hasActiveContext,
} from './otelExporter';

export default function useComponentMetric(pageName) {
  const spanStartRef = useRef(null);
  const hasTrackedRef = useRef(false);
  const mountedAtRef = useRef(null);

  // All mount: avvia span e invia view una tantum
  useEffect(() => {
    if (hasActiveContext()) {
      spanStartRef.current = Date.now();
      // Registra lo span pagina sullo stack: senza questa chiamata
      // trackSpanPageEnd (all'unmount) trova sempre lo span radice in cima
      // allo stack e non invia mai lo span "page:<nome>".
      trackSpanPageStart(pageName);
    }

    // Tempo di permanenza dell'utente sulla pagina: parte sempre al mount,
    // indipendentemente dallo span Tempo (che richiede un contesto attivo).
    mountedAtRef.current = Date.now();

    // Compatibilità col vecchio comportamento: trackView automatica
    if (!hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackComponentView(pageName);
      trackPagePath(pageName);
    }

    return () => {
      // All unmount: invia la durata reale di permanenza sulla pagina
      if (mountedAtRef.current) {
        trackComponentDuration(pageName, Date.now() - mountedAtRef.current);
        mountedAtRef.current = null;
      }

      // All unmount: chiude lo span della pagina
      if (spanStartRef.current && hasActiveContext()) {
        trackSpanPageEnd(pageName, spanStartRef.current * 1000000);
        spanStartRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Metodo manuale per view — utile per re-view o override
  const trackView = useCallback(() => {
    const now = Date.now();
    const elapsed = spanStartRef.current ? now - spanStartRef.current : 0;
    spanStartRef.current = now;

    hasTrackedRef.current = true;
    trackComponentView(pageName, elapsed);
  }, [pageName]);

  const trackError = useCallback((errorMessage, errorStack) => {
    const msg = errorMessage || 'unknown';
    trackComponentError(pageName, msg, errorStack);
    trackSpanPageError(pageName, msg, errorStack);
  }, [pageName]);

  return { trackView, trackError };
}
